import * as app from 'actions/app';
import * as chat from 'actions/chat/';
import * as webRTC from 'actions/chat/webrtc';
import * as interactiveSurvey from 'actions/chat/interactiveSurvey';
import * as fileUpload from 'actions/chat/fileUpload';
import * as bot from 'actions/chat/bot';
import moment from 'moment';
import Immutable from 'immutable';
import globalConfiguration from 'config';
import { interactiveSurveyMessageTypes, interactiveSurveyMessagesSubReducer } from './interactiveSurvey';
import { MESSAGE_FROM, MESSAGE_TYPE, systemMessage, imageMessage, messageFromTPClientAPI, fileMessage } from './message';

const chatConfiguration = globalConfiguration.chat;

const failureActions = [
  chat.START_FAIL,
  chat.START_HUB_FAIL,
  chat.OUT_OF_SCHEDULE_FAIL,
  chat.NETWORK_ERROR,
  chat.HUB_START_ERROR,
  chat.INIT_ERROR,
  bot.FAIL,
];

const loadingActions = [
  chat.OUT_OF_SCHEDULE_REQUEST,
  chat.ROUTING_STOP_WAITING_REQUEST,
];

const interactiveSurveyMessageOnlyActions = [
  interactiveSurvey.WILL_ANSWER,
  interactiveSurvey.WONT_ANSWER,
  interactiveSurvey.ANSWER_ADDITIONAL_DATA,
  interactiveSurvey.SUBMIT,
  interactiveSurvey.SUBMIT_FAIL,
];

const loadingState = { loading: true, error: undefined };
const notLoadingState = { loading: false, error: undefined };

const routingInitialState = {
  routing: false,
  routingAttempts: 0,
  offeredToKeepWaiting: false,
  offeringToKeepWaiting: false,
  routingTimedOut: false,
  queuePosition: undefined,
  stoppedWaiting: false,
  ended: false,
  partEnded: undefined,
  pendingNotifications: 0,
  notifiedEnded: false,
};
const abortRouting = {
  stoppedWaiting: true,
  loading: false,
};

const messageFromAction = (action, from) => ({ from,
  content: action.message,
  type: MESSAGE_TYPE.text,
  name: action.name,
  sent: moment(),
});

export const initialState = {
    loading: false,
    error: undefined,
    started: false,
    hubStarted: false,
    tpClientChatStarted: false,
    schedule: undefined,
    hubStartedTime: undefined,
    ...routingInitialState,
    chatting: false,
    chatted: false,
    messages: Immutable.OrderedSet(),
    agentTyping: false,
    userTyping: false,
    mailTranscriptSent: false,
    overrittenMailTranscriptLabel: undefined,
    notificationsAllowed: false,
    userFocus: true,
    connectionId: undefined,
    modal: undefined,
    images: Immutable.Map(),
    imageBeingShown: undefined,

    // Loaded in chat.START, no need to mess with these ones
    clientConfiguration: undefined,
    queueProperties: undefined,
    client: undefined,
    validationResult: undefined,
    customSiteUrlHexEncoded: undefined,
    rsaExp: undefined,
    rsaMod: undefined,
    tpClientInstanceUrl: undefined,
    delayBetweenRoutingAttempts: undefined,
    satisfactionSurveyCode: undefined,
};

const reducer = (state = initialState, action) => {
  if (failureActions.some(fa => fa === action.type)) {
    return { ...state, loading: false, error: action.error };
  }
  if (loadingActions.some(la => la === action.type)) {
    return { ...state, ...loadingState };
  }
  if (interactiveSurveyMessageOnlyActions.some(ismoa => ismoa === action.type)) {
    return { ...state, messages: interactiveSurveyMessagesSubReducer(state, action) };
  }
  if ([webRTC.CHOOSE_CAMERA, webRTC.CHOOSE_CAMERA_CANCEL, fileUpload.MINIMIZE, chat.CLOSE_MODAL].includes(action.type)) {
    return { ...state, modal: undefined };
  }

  switch (action.type) {
    case chat.CONFIGURATION:
      return { ...state, clientConfiguration: action.configuration };
    case chat.START:
      return { ...state, started: true,
        hubStarted: !state.clientConfiguration.bot
          || !state.clientConfiguration.bot.enabled
          || !state.clientConfiguration.bot.supported
      };
    case chat.START_HUB:
      return { ...state, ...action.chatData, hubStarted: true };
    case chat.START_HUB_HISTORY:
      return { ...state,
        messages: state.messages.concat(action.chatHistory.map(messageFromTPClientAPI))
      };
    case bot.START:
      return { ...state, ...notLoadingState,
        client: { ...action.client },
        queueProperties: { ...action.queueProperties },
        chatting: true
      };
    case chat.OUT_OF_SCHEDULE:
      return { ...state, ...notLoadingState, schedule: action.schedule };
    case chat.HUB_STARTED:
      return { ...state, hubStartedTime: moment(), ...notLoadingState, routing: true };
    case chat.ROUTING_RETRY:
      return { ...state, routingAttempts: action.routingAttempts, queuePosition: action.queuePosition };
    case chat.ROUTING_OFFER_KEEP_WAITING:
      return { ...state, offeredToKeepWaiting: true, offeringToKeepWaiting: true };
    case chat.ROUTING_KEEP_WAITING:
      return { ...state, offeringToKeepWaiting: false };
    case chat.ROUTING_STOP_WAITING_FAIL:
      return { ...state, ...abortRouting, error: action.error };
    case chat.ROUTING_STOP_WAITING:
      return { ...state, ...abortRouting };
    case chat.ROUTING_TIMEOUT:
      return { ...state, ...routingInitialState, routingTimedOut: true };
    case chat.ROUTING_SUCCESS:
      return { ...state, ...routingInitialState, routing: false, chatting: true, connectionId: parseInt(action.connectionId, 10) };
    case chat.MESSAGE_RECEIVED:
      return { ...state, messages: state.messages.add(messageFromAction(action, MESSAGE_FROM.agent)) };
    case chat.MESSAGE_RECEIVED_IMAGE:
      return { ...state,
        messages: state.messages.add(imageMessage({ guid: action.guid, image: action.image })),
        images: state.images.set(action.guid, action.image)
      };
    case chat.MESSAGE_RECEIVED_FILE:
      if (action.printed != null) {
        return state;
      }
      
      return { ...state,
        messages: state.messages.add(fileMessage(action.guid, action.name))
      };
    case chat.MESSAGE_RECEIVED_ERROR:
      return { ...state,
        message: state.messages.add(systemMessage({ content: action.message, bsStyle: 'danger' }))
      };
    case chat.MESSAGE_SENT:
      let message = messageFromAction(action, MESSAGE_FROM.client);
      if (message.content.indexOf('[tpclient') !== -1) {
        return state;
      }
      return { ...state, messages: state.messages.add(message)};
    case chat.AGENT_TYPING:
      return { ...state, agentTyping: true };
    case chat.AGENT_NOT_TYPING:
      return { ...state, agentTyping: false };
    case chat.USER_TYPING:
      return { ...state, userTyping: true };
    case chat.AGENT_NOT_TYPING:
      return { ...state, userTyping: false };
    case chat.SEND_MAIL_TRANSCRIPT_REQUEST:
      return { ...state, overrittenMailTranscriptLabel: state.clientConfiguration.mailTranscriptLoadingLabel };
    case chat.SEND_MAIL_TRANSCRIPT_ERROR:
      return { ...state, mailTranscriptSent: false,
        overrittenMailTranscriptLabel: undefined,
        messages: state.clientConfiguration.mailTranscriptErrorMessage
          ? state.messages.add(systemMessage({ content: state.clientConfiguration.mailTranscriptErrorMessage, bsStyle: 'danger' }))
          : state.messages,
      };
    case chat.SEND_MAIL_TRANSCRIPT:
      return { ...state, mailTranscriptSent: true,
        messages: state.clientConfiguration.mailTranscriptSuccessMessage
          ? state.messages.add(systemMessage({ content: state.clientConfiguration.mailTranscriptSuccessMessage, bsStyle: 'success' }))
          : state.messages,
      };
    case chat.ENDED:
      let newState = { ...state,
        messages: !state.notifiedEnded
          ? state.messages.add(systemMessage({
            content: action.part === chat.CHAT_PART.agent
              ? state.clientConfiguration.agentEndedConversationMessage
              : state.clientConfiguration.customerEndedConversationMessage,
            bsStyle: 'warning'
          })) : state.messages,
        partEnded: action.part,
        notifiedEnded: true,
        modal: undefined,
      };

      return { ...newState, chatting: false, ended: true,
        messages: interactiveSurveyMessagesSubReducer(newState, action)
      };
    case chat.NOTIFICATION_REQUEST_GRANTED:
      return { ...state, notificationsAllowed: true };
    case chat.NOTIFY_MESSAGE_RECEIVED:
      return { ...state, pendingNotifications: state.pendingNotifications + 1 };
    case chat.FOCUS:
      return { ...state, userFocus: true, pendingNotifications: 0 };
    case chat.BLUR:
      return { ...state, userFocus: false };
    case webRTC.CHOOSE_CAMERA_START:
      return { ...state, modal: chat.CHAT_MODAL.chooseCamera };
    case fileUpload.START:
      return { ...state, modal: chat.CHAT_MODAL.fileUpload };
    case fileUpload.UPLOAD_START:
      return { ...state, modal: undefined };
    case fileUpload.UPLOAD:
      return { ...state,
        modal: undefined,
        messages: state.messages.add(systemMessage({
          content: `Se enviaron los archivos: ${action.files.map(f => f.name).join(', ')}`,
          bsStyle: 'success'
        }))
      };
    case fileUpload.RECEIVE_IMAGES:
      return { ...state,
        messages: action.images && action.images.length > 0
          ? state.messages.concat(action.images.map(imageMessage))
          : state.messages
      };
    case chat.SELECT_IMAGE:
      return { ...state, imageBeingShown: action.guid };
    case chat.CLOSE_IMAGE:
      return { ...state, imageBeingShown: undefined };
    case app.RESET:
      return { ...initialState };
    default:
      return state;
  }
};

export default reducer;



// WEBPACK FOOTER //
// ./reducers/chat/main.js